import {
  API_CALL_FAILURE,
  API_CALL_REQUEST,
  API_CALL_SUCCESS,
  CREATE_SEARCH,
  CREATE_SEARCH_SESSION,
  TYPEAHEAD_STREAMING_START,
  TYPEAHEAD_STREAMING_BATCH_UPDATE,
  TYPEAHEAD_STREAMING_COMPLETE,
  TYPEAHEAD_STREAMING_TYPEAHEAD_COMPLETE,
  TYPEAHEAD_STREAMING_ERROR,
  SET_TYPEAHEAD_STREAMING_SOCKET_STATUS,
  SET_TYPEAHEAD_USER_SELECTED_DATES,
  SET_TYPEAHEAD_USER_DATES_INTERACTED,
  SET_TYPEAHEAD_USER_SELECTED_OCCUPANCY,
} from 'actions/actionConstants'
import { FETCH_RECOMMENDATION_SUGGESTIONS, FETCH_USER_RECENT_SEARCHES } from 'actions/apiActionConstants'
import { apiReducerSwitch, createAppReducer } from 'lib/redux/reducerUtils'
import { applyTypeaheadStreamingFieldUpdate } from 'reducers/typeaheadStreamingUpdateUtils'

export const initialSearchState: App.SearchState = {
  searches: {},
  searchSessions: {},
  typeaheadStreamingData: {},
  recommendationSuggestions: {},
  userRecentSearches: {
    state: 'initial',
    data: [],
  },
}

const apiCalls = apiReducerSwitch<App.SearchState>({
  [FETCH_RECOMMENDATION_SUGGESTIONS]: {
    success: (state, action) => ({
      recommendationSuggestions: {
        ...state.recommendationSuggestions,
        [action.key]: action.data,
      },
    }),
  },
  [FETCH_USER_RECENT_SEARCHES]: {
    request: (state, action) => ({
      userRecentSearches: { ...state.userRecentSearches, state: 'loading', cacheKey: action.cacheKey },
    }),
    success: (_, action) => ({
      userRecentSearches: { state: 'done', data: action.data, cacheKey: action.cacheKey },
    }),
    failure: (state, action) => ({
      userRecentSearches: { ...state.userRecentSearches, state: 'error', error: action.error, cacheKey: action.cacheKey },
    }),
  },
})

export default createAppReducer<App.SearchState>(initialSearchState, {
  [API_CALL_REQUEST]: (state, action) => apiCalls(state, action),
  [API_CALL_SUCCESS]: (state, action) => apiCalls(state, action),
  [API_CALL_FAILURE]: (state, action) => apiCalls(state, action),
  [CREATE_SEARCH]: (state, action) => {
    const { key, searchId, searchContext, searchEvent } = action

    return {
      ...state,
      searches: {
        ...state.searches,
        [key]: {
          searchId,
          searchContext: searchContext ?? state.searches[key]?.searchContext,
          searchEvent,
        },
      },
    }
  },
  [CREATE_SEARCH_SESSION]: (state, action) => {
    const { key, searchSessionId } = action

    return {
      ...state,
      searchSessions: {
        ...state.searchSessions,
        [key]: { searchSessionId },
      },
    }
  },
  [TYPEAHEAD_STREAMING_START]: (state, action) => {
    const { typeaheadId, temporaryDoNotExtract } = action
    const currentData = state.typeaheadStreamingData[typeaheadId]
    const nextState = {
      ...currentData,
      typeaheadId,
      isComplete: currentData?.isComplete ?? false,
      streamingError: false,
    }

    if (temporaryDoNotExtract) {
      return {
        ...state,
        typeaheadStreamingData: {
          ...state.typeaheadStreamingData,
          [typeaheadId]: {
            ...nextState,
            typeaheadLoading: true,
          },
        },
      }
    }

    return {
      ...state,
      typeaheadStreamingData: {
        ...state.typeaheadStreamingData,
        [typeaheadId]: {
          ...nextState,
          isComplete: false,
          typeaheadLoading: false,
          streamingError: false,
          completedInput: undefined,
        },
      },
    }
  },
  [TYPEAHEAD_STREAMING_TYPEAHEAD_COMPLETE]: (state, action) => {
    const { typeaheadId } = action
    const currentData = state.typeaheadStreamingData[typeaheadId]

    if (!currentData) {
      return state
    }

    return {
      ...state,
      typeaheadStreamingData: {
        ...state.typeaheadStreamingData,
        [typeaheadId]: {
          ...currentData,
          typeaheadLoading: false,
          streamingError: false,
        },
      },
    }
  },
  [TYPEAHEAD_STREAMING_BATCH_UPDATE]: (state, action) => {
    const { typeaheadId, batch } = action
    const currentData = state.typeaheadStreamingData[typeaheadId]

    if (!currentData || batch.length === 0) {
      return state
    }

    const updatedData: App.TypeaheadStreamingData = { ...currentData }

    for (const entry of batch) {
      applyTypeaheadStreamingFieldUpdate(updatedData, entry)
    }

    return {
      ...state,
      typeaheadStreamingData: {
        ...state.typeaheadStreamingData,
        [typeaheadId]: updatedData,
      },
    }
  },
  [TYPEAHEAD_STREAMING_COMPLETE]: (state, action) => {
    const { typeaheadId, input } = action
    const currentData = state.typeaheadStreamingData[typeaheadId]

    if (!currentData) {
      return state
    }

    return {
      ...state,
      typeaheadStreamingData: {
        ...state.typeaheadStreamingData,
        [typeaheadId]: {
          ...currentData,
          isComplete: true,
          typeaheadLoading: false,
          streamingError: false,
          completedInput: input,
        },
      },
    }
  },
  [TYPEAHEAD_STREAMING_ERROR]: (state, action) => {
    const { typeaheadId } = action
    const currentData = state.typeaheadStreamingData[typeaheadId]

    if (!currentData) {
      return state
    }

    return {
      ...state,
      typeaheadStreamingData: {
        ...state.typeaheadStreamingData,
        [typeaheadId]: {
          ...currentData,
          isComplete: false,
          typeaheadLoading: false,
          streamingError: true,
        },
      },
    }
  },
  [SET_TYPEAHEAD_STREAMING_SOCKET_STATUS]: (state, action) => {
    const { typeaheadId, status } = action
    const currentData = state.typeaheadStreamingData[typeaheadId]

    if (!currentData) {
      // Initialize with required fields if data doesn't exist yet
      return {
        ...state,
        typeaheadStreamingData: {
          ...state.typeaheadStreamingData,
          [typeaheadId]: {
            typeaheadId,
            socketListenersStatus: status,
            isComplete: false,
          },
        },
      }
    }

    return {
      ...state,
      typeaheadStreamingData: {
        ...state.typeaheadStreamingData,
        [typeaheadId]: {
          ...currentData,
          socketListenersStatus: status,
        },
      },
    }
  },
  [SET_TYPEAHEAD_USER_SELECTED_DATES]: (state, action) => {
    const { typeaheadId, dates } = action
    const currentData = state.typeaheadStreamingData[typeaheadId]

    if (!currentData) {
      return {
        ...state,
        typeaheadStreamingData: {
          ...state.typeaheadStreamingData,
          [typeaheadId]: {
            typeaheadId,
            isComplete: false,
            userSelectedDates: dates,
            // Clearing dates (e.g. incompatible NL phrase) also clears interaction ownership.
            userHasInteractedWithDates: !!dates,
          },
        },
      }
    }

    return {
      ...state,
      typeaheadStreamingData: {
        ...state.typeaheadStreamingData,
        [typeaheadId]: {
          ...currentData,
          userSelectedDates: dates,
          userHasInteractedWithDates: !!dates,
        },
      },
    }
  },
  [SET_TYPEAHEAD_USER_DATES_INTERACTED]: (state, action) => {
    const { typeaheadId } = action
    const currentData = state.typeaheadStreamingData[typeaheadId]

    if (!currentData) {
      return {
        ...state,
        typeaheadStreamingData: {
          ...state.typeaheadStreamingData,
          [typeaheadId]: {
            typeaheadId,
            isComplete: false,
            userHasInteractedWithDates: true,
          },
        },
      }
    }

    return {
      ...state,
      typeaheadStreamingData: {
        ...state.typeaheadStreamingData,
        [typeaheadId]: {
          ...currentData,
          userHasInteractedWithDates: true,
        },
      },
    }
  },
  [SET_TYPEAHEAD_USER_SELECTED_OCCUPANCY]: (state, action) => {
    const { typeaheadId, occupancy } = action
    const currentData = state.typeaheadStreamingData[typeaheadId]

    if (!currentData) {
      return {
        ...state,
        typeaheadStreamingData: {
          ...state.typeaheadStreamingData,
          [typeaheadId]: {
            typeaheadId,
            isComplete: false,
            userSelectedOccupancy: occupancy,
          },
        },
      }
    }

    return {
      ...state,
      typeaheadStreamingData: {
        ...state.typeaheadStreamingData,
        [typeaheadId]: {
          ...currentData,
          userSelectedOccupancy: occupancy,
        },
      },
    }
  },
})
